import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { DnsSettings } from '../components/DnsSettings';
import { VerifyBanner } from '../components/VerifyBanner';
import { step } from '../telemetry';
import type { User } from '../types';

interface Props {
  user: User;
  /** Profil dəyişəndə App istifadəçini yenidən yükləsin. */
  onChanged: () => void;
  /** Şifrə dəyişəndə sessiya ölür — girişə qaytarılır. */
  onReauth: () => void;
  /** Gələnlər nişanları açıqdırmı. */
  tabsOn: boolean;
  /**
   * Nişanları aç/söndür.
   *
   * ⚠️ VƏZİYYƏT BURADA SAXLANILMIR, YUXARIDA (`Mail.tsx`) SAXLANILIR.
   * Səbəb: siyahını yükləyən kod oradadır və nişan söndürüləndə siyahı
   * DƏRHAL süzgəcsiz yenidən gəlməlidir. Vəziyyəti burada saxlasaydıq,
   * istifadəçi Ayarlarda söndürüb poçta qayıdanda köhnə, süzülmüş
   * siyahını görərdi və düymənin işləmədiyini düşünərdi.
   */
  onToggleTabs: (on: boolean) => void;
}

function messageFor(code: string): string {
  switch (code) {
    case 'wrong_password':
      return 'Cari şifrə yanlışdır.';
    case 'weak_password':
      return 'Yeni şifrə ən azı 10 simvol olmalıdır.';
    case 'current_required':
      return 'Cari şifrəni yazın.';
    case 'name_too_long':
      return 'Ad çox uzundur.';
    default:
      return 'Əməliyyat alınmadı. Bir azdan yenidən yoxlayın.';
  }
}

/**
 * İmza redaktoru.
 *
 * ⚠️ SADƏ MƏTN — zəngin redaktor DEYİL. Yazma pəncərəsi də düz mətn
 * göndərir; imzada HTML qəbul etsək, məktubun yarısı formatlı, yarısı düz
 * olardı. Zəngin mətn gələndə imza da onunla birlikdə keçiriləcək.
 */
export function Settings({
  user,
  onChanged,
  onReauth,
  tabsOn,
  onToggleTabs,
}: Props): React.ReactElement {
  const [name, setName] = useState(user.displayName ?? '');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameDone, setNameDone] = useState(false);

  /** İmza — serverdə saxlanılır ki, telefondan da eyni olsun. */
  const [sig, setSig] = useState('');
  const [sigBusy, setSigBusy] = useState(false);
  const [sigDone, setSigDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .signature()
      .then((r) => {
        if (!cancelled) setSig(r.signature ?? '');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  async function saveSig(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSigBusy(true);
    setSigDone(false);
    try {
      await api.setSignature(sig.trim() || null);
      setSigDone(true);
    } catch {
      setSigDone(false);
    } finally {
      setSigBusy(false);
    }
  }

  /**
   * MÖVZU REJİMİ.
   *
   * ⚠️ ƏVVƏL YALNIZ `prefers-color-scheme` VARDI — istifadəçinin seçimi
   * yox idi. Əməliyyat sistemi qaranlıq rejimdədirsə, panel də qaranlıq
   * olurdu və bunu dəyişmək mümkün deyildi. Bu, gündüz işıqlı otaqda
   * qaranlıq panelə məhkum olmaq deməkdir.
   *
   * Seçim `localStorage`-da saxlanılır və `<html data-theme>` atributu ilə
   * tətbiq olunur; `auto` seçilsə atribut silinir və sistem qərar verir.
   */
  const [theme, setTheme] = useState<'auto' | 'light' | 'dark'>(
    () => (localStorage.getItem('mailora.theme') as 'auto' | 'light' | 'dark') ?? 'auto',
  );

  useEffect(() => {
    if (theme === 'auto') {
      document.documentElement.removeAttribute('data-theme');
      localStorage.removeItem('mailora.theme');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('mailora.theme', theme);
    }
  }, [theme]);

  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  const [error, setError] = useState<string | null>(null);

  /** Google ilə gələn istifadəçinin şifrəsi olmaya bilər — ilk təyin halı. */
  const noPasswordYet = user.kind === 'user' && !user.mailboxEmail;

  async function saveName(e: FormEvent): Promise<void> {
    e.preventDefault();
    setNameBusy(true);
    setError(null);
    setNameDone(false);
    try {
      step('Profil adı dəyişilir');
      await api.updateProfile(name.trim() || null);
      setNameDone(true);
      onChanged();
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
    } finally {
      setNameBusy(false);
    }
  }

  async function savePassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (next !== again) {
      setError('Yeni şifrələr eyni deyil.');
      return;
    }
    setPwBusy(true);
    try {
      step('Şifrə dəyişilir');
      await api.changePassword(cur || null, next);
      // Server bütün sessiyaları ləğv etdi — yenidən giriş lazımdır.
      onReauth();
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
      setPwBusy(false);
    }
  }

  return (
    <div className="admin">
      <h1>Ayarlar</h1>

      {/*
        ⚠️ TƏSDİQ ZOLAĞI AYARLARDA DA OLMALIDIR.
        Əvvəl yalnız onboarding və poçt ekranında idi. Təsdiq məktubunu
        itirmiş istifadəçi «Ayarlar»a baxır — hesabla bağlı hər şeyin
        orada olacağını gözləyir — və nə vəziyyəti, nə də «yenidən
        göndər» düyməsini tapır. Komponent onsuz da paylaşılandır.
      */}
      <VerifyBanner kind={user.kind} emailVerified={user.emailVerified} />

      {error ? (
        <p className="login-error" role="alert">
          {error}
        </p>
      ) : null}

      <section className="admin-card">
        <h2>Hesab</h2>
        <dl className="kv">
          <dt>Giriş ünvanı</dt>
          <dd>
            <code>{user.email}</code>
          </dd>
          <dt>Poçt qutusu</dt>
          <dd>{user.mailboxEmail ? <code>{user.mailboxEmail}</code> : '—'}</dd>
          <dt>Rol</dt>
          <dd>{user.isTenantAdmin ? 'Şirkət administratoru' : 'İstifadəçi'}</dd>
        </dl>
      </section>

      <section className="admin-card">
        <h2>Görünüş</h2>
        <p className="onboard-note">
          Standart olaraq panel cihazınızın rejiminə uyğunlaşır. İstəsəniz
          özünüz seçə bilərsiniz.
        </p>
        <div className="theme-row">
          {(
            [
              ['auto', 'Cihaza uyğun'],
              ['light', 'İşıqlı'],
              ['dark', 'Qaranlıq'],
            ] as const
          ).map(([v, l]) => (
            <button
              key={v}
              type="button"
              className={`theme-btn${theme === v ? ' is-on' : ''}`}
              onClick={() => setTheme(v)}
              aria-pressed={theme === v}
            >
              {l}
            </button>
          ))}
        </div>

        {/* ── Gələnlər nişanları ──────────────────────────────────────
            ⚠️ SÖNDÜRMƏ İMKANI MƏCBURİDİR. Nişanlar məktubu GİZLƏDİR:
            müştəri «İlkin»də dayanıbsa, «Tanıtımlar»a düşən fakturanı
            görmür. Bu, bizim təsnifatımızın səhvi ola bilər — və
            istifadəçinin ondan qurtulmaq yolu olmalıdır. Onsuz funksiya
            faydadan çox tələyə çevrilir. */}
        <hr className="set-sep" />
        <label className="check">
          <input
            type="checkbox"
            checked={tabsOn}
            onChange={(e) => onToggleTabs(e.target.checked)}
          />
          <span>Gələnlər qutusu nişanlara ayrılsın</span>
        </label>
        <p className="onboard-note">
          İlkin, Sosial, Tanıtımlar və Güncəlliklər nişanları göstərilir.
          Məktublar HEÇ YERƏ köçürülmür — bu, yalnız süzgəcdir və Outlook ilə
          telefonda qutunuz olduğu kimi qalır. Söndürsəniz bütün məktublar
          tək siyahıda görünür.
        </p>
      </section>

      <section className="admin-card">
        <h2>İmza</h2>
        <p className="onboard-note">
          Yeni məktublarınızın sonuna avtomatik əlavə olunur. Boş buraxsanız
          imza istifadə edilmir.
        </p>
        <form onSubmit={saveSig}>
          <textarea
            className="sig-input"
            value={sig}
            maxLength={2000}
            rows={5}
            placeholder={'--\nAd Soyad\nVəzifə · Şirkət\n+994 XX XXX XX XX'}
            onChange={(e) => {
              setSig(e.target.value);
              setSigDone(false);
            }}
          />
          <div className="inline-form">
            <button type="submit" className="btn-primary" disabled={sigBusy}>
              {sigBusy ? 'Saxlanılır…' : 'Saxla'}
            </button>
            {sigDone ? <span className="state is-ok">saxlanıldı</span> : null}
            <span className="dim">{sig.length} / 2000</span>
          </div>
        </form>
      </section>

      <section className="admin-card">
        <h2>Ad</h2>
        <p className="onboard-note">
          Göndərdiyiniz məktublarda bu ad görünür — məsələn{' '}
          <code>{(name.trim() || 'Ad Soyad') + ' <' + (user.mailboxEmail ?? user.email) + '>'}</code>
        </p>
        <form className="inline-form" onSubmit={saveName}>
          <input
            value={name}
            maxLength={120}
            placeholder="Ad Soyad"
            onChange={(e) => {
              setName(e.target.value);
              setNameDone(false);
            }}
          />
          <button type="submit" className="btn-primary" disabled={nameBusy}>
            {nameBusy ? 'Saxlanılır…' : 'Saxla'}
          </button>
          {nameDone ? <span className="state is-ok">saxlanıldı</span> : null}
        </form>
      </section>

      <section className="admin-card">
        <h2>Şifrə</h2>
          {/*
            ⚠️ MƏTN HESAB NÖVÜNDƏN ASILIDIR — ƏVVƏL HAMIYA EYNİ YAZILIRDI.
            Şifrə dəyişmə ucu (`/api/auth/password`) `kind`-ə görə fərqli
            cədvələ yazır: `mailbox` → `mailboxes`, `user` → `users`.
            Yəni şirkət sahibi (panel hesabı) şifrəsini dəyişəndə Outlook
            şifrəsi DƏYİŞMİR. Köhnə mətn hamıya «hər ikisi üçün işlədilir»
            deyirdi — sahib Outlook-un niyə işləmədiyini axtarırdı.
          */}
          <p className="onboard-note">
            {user.kind === 'mailbox' ? (
              <>
                Bu şifrə həm panel, həm də Outlook/iPhone üçün işlədilir.{' '}
                Dəyişdirdikdən sonra <strong>bütün cihazlarda yenidən giriş</strong> lazım
                olacaq.
              </>
            ) : (
              <>
                Bu, <strong>yalnız panel girişinizin</strong> şifrəsidir. Poçt qutularının
                şifrəsi ayrıdır və «Şirkət idarəsi» bölməsindən dəyişdirilir — Outlook və
                iPhone məhz onu işlədir.
              </>
            )}
          </p>
        <form className="stack-form" onSubmit={savePassword}>
          {!noPasswordYet ? (
            <label className="field">
              <span>Cari şifrə</span>
              <input
                type="password"
                autoComplete="current-password"
                value={cur}
                onChange={(e) => setCur(e.target.value)}
              />
            </label>
          ) : null}
          <label className="field">
            <span>Yeni şifrə</span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={10}
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Yeni şifrə (təkrar)</span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={10}
              required
              value={again}
              onChange={(e) => setAgain(e.target.value)}
            />
          </label>
          {next && next.length < 10 ? (
            <p className="field-hint is-warn">Ən azı 10 simvol olmalıdır.</p>
          ) : null}
          <button
            type="submit"
            className="btn-primary"
            disabled={pwBusy || next.length < 10 || next !== again}
          >
            {pwBusy ? 'Dəyişilir…' : 'Şifrəni dəyiş'}
          </button>
        </form>
      </section>

      <section className="admin-card">
        <h2>Poçt proqramı üçün parametrlər</h2>
        <p className="onboard-note">
          Outlook, iPhone və Thunderbird bunları avtomatik tapır — əl ilə qurmaq
          lazım gələrsə aşağıdakılardan istifadə edin.
        </p>
        <dl className="kv">
          <dt>IMAP</dt>
          <dd>
            <code>imap.mailora.az</code> · port <code>993</code> · SSL/TLS
          </dd>
          <dt>SMTP</dt>
          <dd>
            {/*
              ⚠️ 465 / SSL — AUTODISCOVER İLƏ EYNİ OLMALIDIR.
              Əvvəl burada yalnız 587/STARTTLS yazılırdı, `autoconfig.mailora.az`
              isə 465/SSL elan edir. Yəni sistem eyni sualın iki fərqli cavabını
              verirdi: proqram avtomatik quranda 465, müştəri əl ilə quranda 587.
              Hər ikisi işləyir (yoxlanıldı), amma dəstək çağırışında «hansı
              düzgündür?» sualına cavab olmurdu. Tək həqiqət mənbəyi
              autodiscover-dir — RFC 8314 implicit TLS-i tövsiyə edir.
            */}
              <code>smtp.mailora.az</code> · port <code>465</code> · SSL/TLS
            </dd>
            <dt>SMTP (alternativ)</dt>
            <dd>
              <code>smtp.mailora.az</code> · port <code>587</code> · STARTTLS
              <small className="field-hint">
                Köhnə proqramlar 465-i dəstəkləmirsə bunu işlədin.
              </small>
          </dd>
          <dt>İstifadəçi adı</dt>
          <dd>
            <code>{user.mailboxEmail ?? '(qutu yaradılmayıb)'}</code> — tam ünvan
          </dd>
        </dl>
      </section>

      {/*
        DNS QEYDLƏRİ — POÇT PARAMETRLƏRİNİN DAVAMI.
        ⚠️ YER TƏSADÜFİ DEYİL. İkisi də «quraşdırma» sualının cavabıdır:
        yuxarıdakı proqramın içində nə yazılacağını, aşağıdakı domenin
        panelində nə qurulacağını göstərir. Müştəri onları eyni yerdə
        axtarır — sahib də məhz bunu istədi.

        Yalnız şirkət admini görür: DNS ucu serverdə `requireAdmin`-dir.
      */}
      {user.isTenantAdmin && <DnsSettings />}

      {/*
        ⚠️ ÇIXIŞ DÜYMƏSİ AYARLARDA YOX İDİ.
        Yeganə çıxış yolu yan paneldəki adsız avatar ikonu idi — orada nə
        «çıxış» sözü, nə də görünən işarə var; yalnız `title` atributunda
        yazılıb. Toxunma cihazında `title` heç vaxt görünmür, yəni
        istifadəçi hesabından çıxmağın yolunu tapa bilmirdi. Ayarlar
        bunun ən gözlənilən yeridir.
      */}
      <section className="admin-card">
        <h2>Sessiya</h2>
        <p className="onboard-note">
          Ortaq və ya yad kompüterdə işləmisinizsə, çıxış edin — sessiya
          bağlanır və yenidən giriş tələb olunur.
        </p>
        <button className="btn-secondary" onClick={onReauth}>
          Hesabdan çıx
        </button>
      </section>
    </div>
  );
}
